import { AccountInterface, GetTransactionReceiptResponse, RpcProvider } from 'starknet';
import { v4 as uuid } from 'uuid';
import { DecimalFormat } from '@tempusfinance/decimal';
import { Decimal } from '../../datastructures';
import { InvokeResponse, Nullable } from '../../interfaces';
import { waitForTransactionReceipt } from '../../utils';
import { StakeContract } from '../../services/contracts/StakeContract';
import { addToast } from '../toast';
import { createAsyncAtom, emitAtomEvent } from '../../lib/state';
import { pauseBalanceRefresh, resumeBalanceRefresh } from '../lendAndBorrow/pauseBalanceRefresh';
import { stakeWithdrawInput } from './stakeInputAtom';

type StakeStatusName = 'pending-withdraw-tx-approval' | 'withdraw';

type StakeParams = {
  amount: Decimal;
  walletAccount: AccountInterface;
  provider: RpcProvider;
};

export type StakeStatus = {
  status: Nullable<StakeStatusName>;
  params: Nullable<StakeParams>;
  response: Nullable<InvokeResponse>;
  date: Nullable<Date>;
  success?: boolean;
  txnId?: string;
};

const STAKE_STATUS_DEFAULT_VALUE: StakeStatus = {
  params: null,
  status: null,
  response: null,
  date: null,
};

export const stakeStatusAtom = createAsyncAtom<StakeStatus>(async () => STAKE_STATUS_DEFAULT_VALUE);

async function stake(params: StakeParams) {
  const txnId = uuid();

  stakeStatusAtom.setValue({
    params: params,
    status: 'pending-withdraw-tx-approval',
    response: null,
    date: null,
    txnId,
  });

  const stakeContract = new StakeContract(params.provider, params.walletAccount);

  let response: InvokeResponse | null = null;
  try {
    response = await stakeContract.redeem(params.amount);
  } catch (error) {
    // User rejected transaction
    stakeStatusAtom.setValue(STAKE_STATUS_DEFAULT_VALUE);
  }

  if (!response) {
    return;
  }

  const amountFormatted = DecimalFormat.format(params.amount, {
    style: 'currency',
    currency: 'STRK',
    fractionDigits: 2,
    lessThanFormat: true,
    pad: true,
  });

  addToast({
    id: uuid(),
    type: 'info',
    message: `Withdraw ${amountFormatted}`,
    txId: response.transactionHash,
  });

  stakeStatusAtom.setValue({
    params: params,
    status: 'withdraw',
    response: response,
    date: new Date(),
    txnId,
  });

  pauseBalanceRefresh();
  try {
    const receipt = (await waitForTransactionReceipt(
      response.transactionHash,
      params.walletAccount,
      params.provider,
    )) as GetTransactionReceiptResponse;

    if (receipt.isReverted()) {
      stakeStatusAtom.setValue(STAKE_STATUS_DEFAULT_VALUE);

      addToast({
        id: uuid(),
        type: 'error',
        message: `Withdraw ${amountFormatted}`,
        txId: receipt.value.transaction_hash,
      });
      return;
    }

    if (receipt.isSuccess()) {
      addToast({
        id: uuid(),
        type: 'success',
        message: `Withdrew **${amountFormatted}**`,
        txId: receipt.value.transaction_hash,
      });
    }

    emitAtomEvent('unstake');

    stakeWithdrawInput.reset();

    stakeStatusAtom.setValue({
      ...STAKE_STATUS_DEFAULT_VALUE,
      success: true,
      txnId,
    });

    return receipt;
  } catch (error) {
    stakeStatusAtom.setValue(STAKE_STATUS_DEFAULT_VALUE);

    addToast({
      id: uuid(),
      type: 'error',
      message: `Withdraw ${amountFormatted}`,
      txId: response.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
}

export { stake };
